import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

export interface PageLoadingProps {
  /**
   * Ancora o overlay ao ancestral posicionado mais próximo, em vez de cobrir a viewport.
   * Os shells com sidebar usam isso para cobrir só a coluna de conteúdo.
   */
  readonly contained?: boolean;
}

// O CSS global de `.page-loading` (index.html e _centerweb.scss) assume o layout
// legado: `position: fixed` e `margin-left: 248px` para desviar da sidebar do Angular.
// Aqui a sidebar é outra coluna do shell, então o recuo é zerado. Inline de propósito:
// vence a regra global sem depender de ordem de folha de estilo nem de especificidade.
const CONTAINED_STYLE: CSSProperties = { position: 'absolute', marginLeft: 0 };
const FULLSCREEN_STYLE: CSSProperties = { marginLeft: 0 };

/**
 * Overlay de carregamento que o `LoadService` da folha Angular aciona.
 *
 * `load.habilitar()`/`desabilitar()` não usam estado: o `exibirImagemLoading` faz
 * `$('.page-loading').addClass('loadIn')` via jQuery. Na folha não há FullLayout, que é
 * quem renderizava esse elemento, então sem ele a chamada vira no-op silencioso. Este
 * componente repõe o elemento com a mesma marcação (`.page-loading`, `.loading-icon`,
 * `.load-text`), e as telas legadas voltam a exibir o spinner sem alteração.
 *
 * `className` precisa ser estático: o React reescreve o atributo inteiro quando o valor
 * muda, o que apagaria a classe `loadIn` posta pelo jQuery.
 */
export function PageLoading({ contained = false }: PageLoadingProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      className="page-loading"
      role="status"
      style={contained ? CONTAINED_STYLE : FULLSCREEN_STYLE}
    >
      <img src="assets/img/oval.svg" className="loading-icon" alt="" />
      <span className="load-text">{t('pageLoading.message')}</span>
    </div>
  );
}
